import React, { useState } from "react";
import { setTitle } from "../../helpers/MetaTag";
import useFetch from "../../hooks/useFetch";
import dayjs from "dayjs";
import { useDebounce } from "../../hooks/useDebounce";
import {
  donorSubsciptionStatusChange,
  mySubscriptionList,
} from "../../services/subscriptionService";
import { useSelector } from "react-redux";
import { donorTransactionList } from "../../services/transactionService";
import LimitSelect from "../../components/elements/LimitSelect";
import { handleFilterChange } from "../../helpers";
import Search from "../../components/form/Search";
import Table from "../../components/elements/table/Table";
import Switch from "../../components/form/Switch";
import Badge from "../../components/elements/Badge";
import SubscriptionModal from "../../components/sections/admin/subscription/SubscriptionModal";
import { toast } from "react-toastify";
import useMutation from "../../hooks/useMutation";
import { useNavigate } from "react-router-dom";
import Button from "../../components/form/Button";
import { pdf } from "@react-pdf/renderer"; // your PDF document
import fileSaver from "file-saver"; // to trigger download
import DonationReciptPdf from "../../pdfs/DonationReciptPdf";

const Dashboard = () => {
  setTitle("Vpeters User | Recurring Donations");
  const [keyword, setKeyword] = useState("");
  const [filters, setFilters] = useState({
    limit: 5,
    page: 1,
    sortQuery: "-createdAt",
  });
  const search = useDebounce(keyword, 300);
  const { data } = useSelector((state) => state?.profile);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const navigate = useNavigate();

  const {
    data: transactionList,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useFetch({
    dependencies: ["donation", filters, search],
    fetchFunction: async () => {
      const res = await donorTransactionList({
        ...filters,
        categoryType: "donation",
        isRecurring: true,
        ...(search && { keyword: search }),
      });
      return res?.docs;
    },
  });

  const {
    data: subscriptionData,
    isLoading: timelineLoading,
    updateData,
  } = useFetch({
    dependencies: ["subscriptionData"],
    fetchFunction: async () => {
      const res = await mySubscriptionList();
      return res;
    },
  });

  const handleDownloadReceipt = async (item) => {
    const fileName = `Receipt-${item?.invoiceId || "N/A"}.pdf`;
    const blob = await pdf(<DonationReciptPdf data={item} />).toBlob();
    fileSaver.saveAs(blob, fileName);
  };

  const tableHeadData = [
    {
      _id: 2,
      name: "Donor Name",
      align: "left",
    },
    {
      _id: 3,
      name: "Email",
      align: "left",
    },
    {
      _id: 4,
      name: "Amount",
      align: "left",
    },
    {
      _id: 5,
      name: "Category",
      align: "left",
    },
    {
      _id: 6,
      name: "Type",
      align: "left",
    },
    {
      _id: 7,
      name: "Organization Name",
      align: "left",
    },
    {
      _id: 8,
      name: "Date",
      align: "left",
    },
    {
      _id: 9,
      name: "Payment Method",
      align: "left",
    },
    {
      _id: 10,
      name: "Status",
      align: "left",
    },
    {
      _id: 11,
      name: "Auto Email Sent",
      align: "left",
    },
    {
      _id: 12,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = transactionList?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: "$" + item?.amount || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.donationFrequency?.charAt(0).toUpperCase() +
              item?.donationFrequency?.slice(1) || "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.donationType?.charAt(0).toUpperCase() +
              item?.donationType?.slice(1) || "-",
        },
        {
          _id: 7,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.organizationName || "-",
        },

        {
          _id: 8,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.date ? dayjs(item?.date).format("DD MMM, YYYY") : "-",
        },
        {
          _id: 9,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.paymentMethod
              ?.split("_") // Split by underscore
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
              .join(" ") || "-",
        },
        {
          _id: 10,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.paymentStatus.charAt(0).toUpperCase() +
              item?.paymentStatus.slice(1) || "-",
        },
        {
          _id: 11,
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px]",
          data: item?.isEmail ? (item?.isEmail === true ? "Yes" : "No") : "-",
        },
        {
          _id: 12,
          type: "action",
          align: "center",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: 1,
              name: "Download Receipt",
              icon: "fa-regular fa-download",
              onClick: () => {
                handleDownloadReceipt(item);
                // navigate(`/donor/donations/pdf`, {
                //   state: {
                //     data: item,
                //     type: "DonationReciptPdf",
                //     fileNmae: "Download-Donation-Receipt.pdf",
                //   },
                // });
              },
            },
          ],
        },
      ],
    };
  });

  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await donorSubsciptionStatusChange({
          subscriptionId: id,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status === 200) {
          updateData((pre) => {
            console.log(pre);

            pre.data =
              pre.data?._id === payload?.id
                ? { ...pre.data, status: payload?.status === 1 ? 4 : 1 }
                : pre.data;
            return { ...pre };
          });
          setDeleteModal((pre) => ({ data: null, isOpen: false }));
        } else {
          toast.warning(res?.message || res?.error);
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  return (
    <>
      <h1 className="text-3xl font-bold text-[#C94E53]  px-6 py-4 rounded-2xl text-center ">
        Recurring Donations
      </h1>
      <h1 className="text-base font-bold px-3 py-2 rounded-xl  bg-white mb-4">
        Thank you for inspiring tomorrow with your actions today. Your dashboard
        displays your action history and allows you to manage any recurring
        actions.
      </h1>
      {subscriptionData?.data ? (
        <div className="bg-white shadow-lg rounded-2xl p-6 mx-auto border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {data?.firstName + " " + data?.lastName}
              </h2>
              <p className="text-base text-gray-500">{data?.email}</p>
            </div>
            <div className="flex gap-2 items-center">
              <Switch
                className={
                  "!w-10 !h-5 before:!w-4 before:!h-4 peer-checked:before:!left-[22px]"
                }
                checked={subscriptionData?.data?.status === 1}
                onChange={() => {
                  setDeleteModal((pre) => ({
                    ...pre,
                    data: subscriptionData?.data,
                    isOpen: true,
                  }));
                }}
                disabled={subscriptionData?.data?.status === 4}
              />
              <Badge
                type={
                  subscriptionData?.data?.status === 1 ? "success" : "danger"
                }
                label={
                  subscriptionData?.data?.status === 1 ? "Active" : "Cancel"
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <p className="text-gray-500">Amount</p>
              <p className="font-medium">
                {subscriptionData?.data?.amount
                  ? "$" + subscriptionData?.data?.amount
                  : "-"}
              </p>
            </div>

            <div>
              <p className="text-gray-500">Subscription Date</p>
              <p className="font-medium">
                {dayjs(subscriptionData?.data?.startDate).format(
                  "DD MMM, YYYY"
                ) || "-"}
              </p>
            </div>

            <div>
              <p className="text-gray-500">Duration</p>
              <p className="font-medium">
                {subscriptionData?.data?.donationFrequency
                  ?.charAt(0)
                  ?.toUpperCase() +
                  subscriptionData?.data?.donationFrequency?.slice(1) || "-"}
              </p>
            </div>

            <div>
              <p className="text-gray-500">Next Funding Date</p>
              <p className="font-medium">
                {dayjs(subscriptionData?.data?.endDate).format(
                  "DD MMM, YYYY"
                ) || "-"}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h4 className="text-lg font-medium text-red-500">
            No Reccurring Donation Yet!.
          </h4>
        </div>
      )}

      <div className="my-4">
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center gap-3">
            {/* <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={transactionList?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div> */}
          </div>
          {data?.donation && (
            <Button
              buttonType={"button"}
              buttonLabel={"View More"}
              buttonClasses={
                "!h-10 !px-4 !bg-[#C94E53] !text-white !rounded-md"
              }
              buttonLink={"/donor/subscription"}
              buttonHasLink={true}
            />
          )}
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          // pageCount={transactionList?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          isFetching={isFetching}
          // isOrderingDisabled={true}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          handleOrdering={(newData, oldData) => {
            // donationOrdering(newData, oldData);
          }}
          originalData={[...(transactionList?.data || [])]}
          setOriginalData={(data) =>
            updateData((pre) => {
              pre.data = data;
              return { ...pre };
            })
          }
        />
      </div>
      <SubscriptionModal
        open={deleteModal?.isOpen}
        loading={statusChangeInPending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        title="Cancel Subscribtion"
        customMessage="Confirm Cancel subscription."
        deleteHandler={() => {
          statusChangeHandler({
            id: deleteModal?.data?._id,
            status: deleteModal?.data?.status,
          });
        }}
      />
    </>
  );
};

export default Dashboard;
